import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Copy, Download, Edit, Printer, Receipt, Send, Trash2 } from 'lucide-react';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import QuotationSendModal from '../../components/QuotationSendModal';
import {
  deleteQuotation,
  getQuotationById,
  copyQuotation,
  convertQuotationToInvoice,
  downloadQuotationPdf,
} from '../../utils/sales/quotationApi';
import { printQuotation } from '../../utils/sales/printQuotation';
import {
  canManageQuotations,
  canConvertQuotationToInvoice,
} from '../../utils/salesTransactionPermissions';
import {
  getQuotationCustomerName,
  type Quotation,
} from '../../types/sales/quotation';
import type { SalesTransactionAction } from '../../types/salesTransaction';
import { toast } from '../../utils/toast';
import { useSalesTransactionsHub } from './SalesTransactionsHubContext';
import {
  PreviewActionFooter,
  PreviewCollapsibleLineItems,
  PreviewFinancialHero,
  PreviewMetaGrid,
  PreviewMetaItem,
  PreviewSkeleton,
  previewActionClasses,
} from '../../components/transactions/TransactionPreviewShared';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const quotationStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'SENT':
      return 'bg-indigo-100 text-indigo-800';
    case 'ACCEPTED':
      return 'bg-green-100 text-green-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-700';
    case 'EXPIRED':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

type PreviewLocationState = {
  availableActions?: SalesTransactionAction[];
};

const SalesQuotationPreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useSalesTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const routeState = location.state as PreviewLocationState | null;

  const quotationId = id != null ? Number(id) : Number.NaN;

  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  const availableActions = useMemo(
    () => routeState?.availableActions ?? [],
    [routeState?.availableActions]
  );

  const canEdit = useMemo(() => {
    if (availableActions.includes('edit')) return true;
    return quotation != null && canManageQuotations();
  }, [availableActions, quotation]);

  const canDelete = availableActions.includes('delete');

  const canCopy = canManageQuotations();

  const canSend = canManageQuotations();

  const canConvert = canConvertQuotationToInvoice();

  const close = useCallback(() => {
    navigate({ pathname: '/sales-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const loadQuotation = useCallback(async () => {
    if (!id || Number.isNaN(quotationId)) {
      setError('Invalid quotation ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setQuotation(await getQuotationById(quotationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quotation');
      setQuotation(null);
    } finally {
      setLoading(false);
    }
  }, [id, quotationId]);

  useEffect(() => {
    void loadQuotation();
  }, [loadQuotation]);

  const openEdit = () => {
    if (!quotation || !canEdit) return;
    navigate(
      { pathname: `/sales-transactions/quotation/${quotation.id}`, search: listSearch },
      { preventScrollReset: true }
    );
  };

  const handleDelete = async () => {
    if (!quotation || !canDelete) return;
    if (
      !window.confirm(
        `Delete quotation ${quotation.quotation_number}? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteQuotation(quotation.id);
      refetchList();
      close();
      toast.success(`Quotation ${quotation.quotation_number} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!quotation || !canCopy) return;
    if (
      !window.confirm(
        `Copy quotation ${quotation.quotation_number}? A new draft will be created.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const copied = await copyQuotation(quotation.id);
      refetchList();
      toast.success(`Copied to ${copied.quotation_number}.`);
      navigate(
        { pathname: `/sales-transactions/quotation/${copied.id}`, search: listSearch },
        { preventScrollReset: true }
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to copy quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!quotation || !canConvert) return;
    if (
      !window.confirm(
        `Convert quotation ${quotation.quotation_number} to a draft invoice? Tax will be recalculated. The quotation will not be changed.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const invoice = await convertQuotationToInvoice(quotation.id);
      refetchList();
      toast.success(`Created invoice ${invoice.invoice_number}.`);
      navigate(
        { pathname: `/sales-transactions/invoice/${invoice.id}`, search: listSearch },
        { preventScrollReset: true }
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to convert quotation to invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!quotation) return;
    try {
      setActionLoading(true);
      await downloadQuotationPdf(quotation.id, `${quotation.quotation_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download quotation PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!quotation) return;
    try {
      setActionLoading(true);
      await printQuotation(quotation);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSent = (updated: Quotation) => {
    setQuotation(updated);
    refetchList();
  };

  const lines = quotation?.lines ?? [];

  const actionFooter =
    quotation && !loading && !error ? (
      <PreviewActionFooter>
        {canEdit && (
          <button
            type="button"
            onClick={openEdit}
            disabled={actionLoading}
            className={previewActionClasses.primary}
          >
            <Edit className="h-4 w-4" />
            Edit quotation
          </button>
        )}
        {canConvert && (
          <button
            type="button"
            onClick={handleConvertToInvoice}
            disabled={actionLoading || lines.length === 0}
            className={previewActionClasses.secondary}
          >
            <Receipt className="h-4 w-4" />
            Convert to invoice
          </button>
        )}
        {canCopy && (
          <button
            type="button"
            onClick={handleCopy}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <Copy className="h-4 w-4" />
            Copy
          </button>
        )}
        <button
          type="button"
          onClick={() => void handlePrint()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Printer className="h-4 w-4" />
          Print
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadPdf()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Download className="h-4 w-4" />
          Download
        </button>
        {canSend && (
          <button
            type="button"
            onClick={() => setShowSendModal(true)}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <Send className="h-4 w-4" />
            Send
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={actionLoading}
            className={previewActionClasses.dangerStrong}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        )}
        <button type="button" onClick={close} className={previewActionClasses.close}>
          Close
        </button>
      </PreviewActionFooter>
    ) : undefined;

  return (
    <>
      <TransactionOverlay
        variant="preview"
        titleMono
        title={quotation?.quotation_number ?? 'Quotation'}
        subtitle={
          quotation
            ? `${getQuotationCustomerName(quotation)} · ${formatDate(quotation.quote_date)}`
            : 'Loading…'
        }
        footer={actionFooter}
        onClose={close}
      >
        {loading ? (
          <PreviewSkeleton />
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : quotation ? (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${quotationStatusColor(quotation.status)}`}
              >
                {formatLabel(quotation.status)}
              </span>
            </div>

            <PreviewFinancialHero
              amounts={[{ label: 'Total', amount: quotation.total_amount }]}
            />

            <PreviewMetaGrid>
              <PreviewMetaItem label="Customer">
                <span className="font-medium">{getQuotationCustomerName(quotation)}</span>
              </PreviewMetaItem>
              <PreviewMetaItem label="Valid until">
                {quotation.valid_until ? formatDate(quotation.valid_until) : '—'}
              </PreviewMetaItem>
            </PreviewMetaGrid>

            {lines.length > 0 && (
              <PreviewCollapsibleLineItems
                lines={lines.map((line) => ({
                  id: line.id,
                  name: line.item_snapshot.name,
                  sku: line.item_snapshot.sku,
                  qty: line.quantity,
                  unitPrice: line.unit_price,
                  lineTotal: line.line_total,
                }))}
              />
            )}

            {quotation.memo && (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-700">Memo:</span> {quotation.memo}
              </p>
            )}

            {quotation.notes && (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-700">Notes:</span> {quotation.notes}
              </p>
            )}
          </div>
        ) : null}
      </TransactionOverlay>

      {quotation && (
        <QuotationSendModal
          quotation={quotation}
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          onSent={handleSent}
        />
      )}
    </>
  );
};

export default SalesQuotationPreview;
